import type { InputSelectDropdownOption } from 'components/InputSelectDropdown/InputSelectDropdown';

/**
 * Well-known cities to prioritize per UTC offset (in minutes).
 * These appear first in the label; remaining IANA cities follow for search.
 */
const PRIORITY_CITIES: Record<number, string[]> = {
  [-720]: ['Baker Island', 'Howland Island'],
  [-660]: ['Pago Pago', 'Niue'],
  [-600]: ['Honolulu', 'Tahiti', 'Rarotonga'],
  [-540]: ['Anchorage'],
  [-480]: ['Los Angeles', 'Vancouver', 'Seattle', 'Tijuana'],
  [-420]: ['Denver', 'Phoenix', 'Calgary', 'Chihuahua'],
  [-360]: ['Chicago', 'Mexico City', 'Winnipeg'],
  [-300]: ['New York', 'Toronto', 'Lima', 'Panama', 'Havana'],
  [-240]: ['Santiago', 'La Paz', 'Halifax', 'San Juan', 'Caracas'],
  [-180]: ['Sao Paulo', 'Buenos Aires', 'Montevideo', 'Cayenne'],
  [-120]: ['Fernando de Noronha', 'South Georgia'],
  [-60]: ['Azores', 'Cape Verde'],
  [0]: ['London', 'Dublin', 'Lisbon', 'Reykjavik', 'Accra', 'Abidjan'],
  [60]: ['Berlin', 'Paris', 'Rome', 'Madrid', 'Amsterdam', 'Warsaw', 'Lagos'],
  [120]: ['Cairo', 'Johannesburg', 'Helsinki', 'Athens', 'Kyiv', 'Bucharest', 'Jerusalem'],
  [180]: ['Moscow', 'Istanbul', 'Nairobi', 'Riyadh', 'Baghdad', 'Kuwait', 'Minsk'],
  [240]: ['Dubai', 'Baku', 'Tbilisi', 'Yerevan', 'Muscat'],
  [300]: ['Karachi', 'Tashkent', 'Yekaterinburg', 'Ashgabat'],
  [360]: ['Dhaka', 'Almaty', 'Omsk'],
  [420]: ['Bangkok', 'Jakarta', 'Hanoi', 'Ho Chi Minh', 'Krasnoyarsk'],
  [480]: ['Singapore', 'Beijing', 'Hong Kong', 'Kuala Lumpur', 'Perth', 'Manila', 'Taipei'],
  [540]: ['Tokyo', 'Seoul', 'Yakutsk'],
  [600]: ['Sydney', 'Melbourne', 'Brisbane', 'Port Moresby', 'Guam'],
  [660]: ['Noumea', 'Honiara', 'Magadan'],
  [720]: ['Auckland', 'Suva', 'Kamchatka'],
  [780]: ["Nuku'alofa", 'Apia', 'Fakaofo'],
  [840]: ['Kiritimati']
};

/**
 * Parse UTC offset from Intl formatted timezone name.
 * e.g., "GMT+5:30" → 330, "GMT-5" → -300, "GMT" → 0
 */
function parseGmtOffset(gmtString: string): number {
  if (gmtString === 'GMT') return 0;
  const match = gmtString.match(/^GMT([+-])(\d{1,2})(?::(\d{2}))?$/);
  if (!match) return 0;
  const sign = match[1] === '+' ? 1 : -1;
  const hours = parseInt(match[2], 10);
  const minutes = parseInt(match[3] || '0', 10);
  return sign * (hours * 60 + minutes);
}

/**
 * Get the UTC offset in minutes for a given IANA timezone.
 * Uses a fixed reference date in January to avoid DST ambiguity.
 */
function getTimezoneOffsetMinutes(timeZone: string): number {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    timeZoneName: 'shortOffset'
  });
  const parts = formatter.formatToParts(new Date('2025-01-15T12:00:00Z'));
  const tzPart = parts.find((p) => p.type === 'timeZoneName');
  return parseGmtOffset(tzPart?.value || 'GMT');
}

/**
 * Extract city name from IANA timezone ID.
 * e.g., "America/New_York" → "New York", "Asia/Ho_Chi_Minh" → "Ho Chi Minh"
 */
function getCityName(tz: string): string {
  const parts = tz.split('/');
  return parts[parts.length - 1].replace(/_/g, ' ');
}

/**
 * Format UTC offset string from offset hours.
 * e.g., 5 → "+05:00", -5 → "-05:00", 0 → "+00:00"
 */
export function formatOffsetString(offsetHours: number): string {
  const sign = offsetHours >= 0 ? '+' : '-';
  const abs = Math.abs(offsetHours);
  const h = String(Math.floor(abs)).padStart(2, '0');
  const m = String(Math.round((abs % 1) * 60)).padStart(2, '0');
  return `${sign}${h}:${m}`;
}

/**
 * Generate timezone dropdown options using native Intl API (IANA database).
 * Prioritizes well-known cities first, then includes all IANA cities for search.
 * Filtered to whole-hour offsets only (BE limitation: popUpStartHourUTC is integer 0-23).
 * Sorted from UTC-12:00 to UTC+14:00.
 */
export function getTimezoneOptions(): InputSelectDropdownOption[] {
  const timezones: string[] = (Intl as any).supportedValuesOf('timeZone');
  const offsetMap = new Map<number, string[]>();

  for (const tz of timezones) {
    // Skip entries without a region (e.g., "EST", "CET")
    if (!tz.includes('/')) continue;

    const offsetMinutes = getTimezoneOffsetMinutes(tz);

    // Filter out non-whole-hour offsets (BE only supports integers)
    if (offsetMinutes % 60 !== 0) continue;

    const city = getCityName(tz);
    const existing = offsetMap.get(offsetMinutes) || [];
    existing.push(city);
    offsetMap.set(offsetMinutes, existing);
  }

  return Array.from(offsetMap.entries())
    .sort(([a], [b]) => a - b)
    .map(([offsetMinutes, ianaCities]) => {
      const offsetHours = offsetMinutes / 60;
      const offsetStr = formatOffsetString(offsetHours);

      // Prioritize well-known cities, then append remaining IANA cities
      const priority = PRIORITY_CITIES[offsetMinutes] || [];
      const prioritySet = new Set(priority.map((c) => c.toLowerCase()));
      const remaining = ianaCities.filter(
        (c) => !prioritySet.has(c.toLowerCase())
      );
      const allCities = [...priority, ...remaining];

      // Label: first ~4 cities for display, full list for search (truncated by CSS)
      const label = `(UTC ${offsetStr}) ${allCities.join(', ')}`;

      return {
        value: offsetStr,
        label
      };
    });
}

/**
 * Convert UTC offset hours to popUpStartHourUTC.
 * Reset happens at midnight in the selected timezone.
 * e.g., UTC+5 → 19, UTC-5 → 5, UTC+0 → 0
 */
export function offsetHoursToPopUpStartHour(offsetHours: number): number {
  return ((24 - offsetHours) % 24 + 24) % 24;
}

/**
 * Convert popUpStartHourUTC back to UTC offset hours.
 * e.g., 19 → +5, 5 → -5, 0 → 0
 */
export function popUpStartHourToOffsetHours(hour: number): number {
  let offset = -hour;
  if (offset < -12) offset += 24;
  return offset;
}

/**
 * Convert popUpStartHourUTC to the dropdown option value string.
 * e.g., 19 → "+05:00", 5 → "-05:00", 0 → "+00:00"
 */
export function popUpStartHourToOptionValue(hour: number): string {
  const offsetHours = popUpStartHourToOffsetHours(hour);
  return formatOffsetString(offsetHours);
}

/**
 * Parse an offset string like "+05:00" or "-05:00" back to offset hours.
 */
export function parseOffsetString(offsetStr: string): number {
  const match = offsetStr.match(/^([+-])(\d{2}):(\d{2})$/);
  if (!match) return 0;
  const sign = match[1] === '+' ? 1 : -1;
  const hours = parseInt(match[2], 10);
  const minutes = parseInt(match[3], 10);
  return sign * (hours + minutes / 60);
}
